/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Cross2Icon, CheckCircledIcon, IdCardIcon, CalendarIcon } from '@/shared/ui/radix-icons';
import { Text, Title, UnstyledButton, FilterPanelShell } from '@/shared/ui';
import { VISIT_ACCENT_COLORS, useVisitStatusLabels } from '../constants/visit-colors';
import type { VisitStatus } from '../constants/visit-colors';

export interface VisitFilters {
  statuses?: VisitStatus[];
  brokers?: string[];
  period?: string;
}

/**
 * The period the board opens on. Anything else counts as an active filter,
 * the same way the other listing panels only count a narrowed value.
 */
export const DEFAULT_VISIT_PERIOD = 'thisWeek';

interface VisitFilterPanelProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: VisitFilters) => void;
  onClear: () => void;
  initialFilters?: VisitFilters;
  members?: Array<{ email: string; first_name?: string; last_name?: string; name?: string }>;
  periodOptions: Array<{ key: string; label: string }>;
}

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <Title
    order={4}
    style={{
      display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
      fontSize: 'var(--text-meta)', fontWeight: 'var(--font-bold)' as unknown as number,
      color: 'var(--text-primary)', margin: 0, marginBottom: 'var(--space-2)',
    }}
  >
    <Text as="span" style={{ color: 'var(--seed-base)', display: 'flex' }}>{icon}</Text>
    {title}
  </Title>
);

export const VisitFilterPanel: React.FC<VisitFilterPanelProps> = ({
  open, onClose, onApply, onClear, initialFilters = {}, members = [],
  periodOptions,
}) => {
  const { t } = useTranslation('features/visits');
  const statusLabels = useVisitStatusLabels();
  const [filters, setFilters] = useState<VisitFilters>(initialFilters);

  useEffect(() => {
    setFilters(initialFilters);
  }, [open]);

  const toggleStatus = (status: VisitStatus) => {
    const current = filters.statuses || [];
    const next = current.includes(status)
      ? current.filter((s) => s !== status)
      : [...current, status];
    setFilters({ ...filters, statuses: next });
  };

  const toggleBroker = (email: string) => {
    const current = filters.brokers || [];
    const next = current.includes(email)
      ? current.filter((b) => b !== email)
      : [...current, email];
    setFilters({ ...filters, brokers: next });
  };

  const period = filters.period ?? DEFAULT_VISIT_PERIOD;
  const activeCount =
    (filters.statuses?.length || 0) +
    (filters.brokers?.length || 0) +
    (period === DEFAULT_VISIT_PERIOD ? 0 : 1);

  if (!open) return null;

  return (
    <FilterPanelShell
      testIdPrefix="visit-"
      layout="sibling"
      title={t('filters.title')}
      onClose={onClose}
      onClear={() => { setFilters({ period: DEFAULT_VISIT_PERIOD }); onClear(); }}
      onApply={() => { onApply(filters); onClose(); }}
      activeCount={activeCount}
      clearLabel={t('filters.clear')}
      applyLabel={t('filters.apply', { count: activeCount })}
      closeIcon={<Cross2Icon width={14} height={14} />}
      ButtonComponent={UnstyledButton}
      TextComponent={({ children, style }) => (
        <Text as="span" style={style ?? { fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-bold)' as unknown as number, color: 'var(--seed-base)' }}>
          {children}
        </Text>
      )}
      backdropStyle={{
        /*
         * The whole viewport, like every other filter panel. It used to
         * start one `--header-height` down, from when a global top bar sat
         * above these routes; the agency shell draws its own now, so the
         * offset left this panel floating below a strip of page while the
         * listings panel beside it covered the screen.
         */
        position: 'fixed', inset: 0,
        background: 'var(--overlay-scrim)', backdropFilter: 'blur(2px)', zIndex: 100,
      }}
      panelStyle={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: '340px', maxWidth: '90vw', background: 'var(--surface-primary)',
        boxShadow: 'var(--shadow-lg)', zIndex: 101,
        display: 'flex', flexDirection: 'column', fontFamily: 'var(--font-family)', overflow: 'hidden',
      }}
      headerStyle={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'var(--space-3) var(--space-4)',
        background: 'color-mix(in srgb, var(--seed-base) 5%, var(--surface-primary))',
        borderBottom: '1px solid color-mix(in srgb, var(--seed-base) 10%, transparent)',
      }}
      closeButtonStyle={{ width: 28, height: 28, borderRadius: 'var(--radius-md)', border: 'none', background: 'transparent', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
      bodyStyle={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}
      footerStyle={{ display: 'flex', gap: 'var(--space-2)', padding: 'var(--space-3) var(--space-4)', borderTop: '1px solid var(--border-subtle)' }}
      clearButtonStyle={{ flex: 1, padding: 'var(--space-2)', fontSize: 'var(--text-meta)', fontWeight: 'var(--font-semibold)' as unknown as number, background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontFamily: 'var(--font-family)' }}
      applyButtonStyle={{ flex: 1, padding: 'var(--space-2)', fontSize: 'var(--text-meta)', fontWeight: 'var(--font-bold)' as unknown as number, background: 'var(--button-primary-bg)', color: 'var(--button-primary-text)', border: 'none', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontFamily: 'var(--font-family)' }}
    >
      {/* Período */}
          <div>
            <SectionHeader icon={<CalendarIcon width={14} height={14} />} title={t('filters.period')} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              {periodOptions.map((opt) => {
                const active = period === opt.key;
                return (
                  <UnstyledButton
                    key={opt.key}
                    type="button"
                    onClick={() => setFilters({ ...filters, period: opt.key })}
                    style={{
                      padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--text-meta)', fontWeight: active ? ('var(--font-semibold)' as unknown as number) : ('var(--font-medium)' as unknown as number),
                      fontFamily: 'var(--font-family)', cursor: 'pointer', transition: 'var(--transition-standard)',
                      background: active ? 'color-mix(in srgb, var(--seed-base) 8%, var(--surface-primary))' : 'transparent',
                      color: active ? 'var(--seed-base)' : 'var(--text-secondary)',
                      border: active ? '1.5px solid color-mix(in srgb, var(--seed-base) 40%, transparent)' : '1px solid var(--border-subtle)',
                    }}
                  >
                    {opt.label}
                  </UnstyledButton>
                );
              })}
            </div>
          </div>

      {/* Status */}
          <div>
            <SectionHeader icon={<CheckCircledIcon width={14} height={14} />} title={t('filters.status')} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              {(Object.keys(VISIT_ACCENT_COLORS) as VisitStatus[]).map((status) => {
                const active = (filters.statuses || []).includes(status);
                const color = VISIT_ACCENT_COLORS[status];
                return (
                  <UnstyledButton
                    key={status}
                    type="button"
                    onClick={() => toggleStatus(status)}
                    style={{
                      padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--text-meta)', fontWeight: active ? ('var(--font-semibold)' as unknown as number) : ('var(--font-medium)' as unknown as number),
                      fontFamily: 'var(--font-family)', cursor: 'pointer', transition: 'var(--transition-standard)',
                      background: active ? `${color}28` : 'transparent',
                      color: active ? color : 'var(--text-secondary)',
                      border: active ? `1.5px solid ${color}` : '1px solid var(--border-subtle)',
                    }}
                  >
                    {statusLabels[status]}
                  </UnstyledButton>
                );
              })}
            </div>
          </div>

          {/* Corretor */}
          {members.length > 0 && (
            <div>
              <SectionHeader icon={<IdCardIcon width={14} height={14} />} title={t('filters.broker')} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                {members.map((m) => {
                  const active = (filters.brokers || []).includes(m.email);
                  const displayName = m.name || [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
                  return (
                    <UnstyledButton
                      key={m.email}
                      type="button"
                      onClick={() => toggleBroker(m.email)}
                      style={{
                        padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--text-meta)', fontWeight: active ? ('var(--font-semibold)' as unknown as number) : ('var(--font-medium)' as unknown as number),
                        fontFamily: 'var(--font-family)', cursor: 'pointer', transition: 'var(--transition-standard)',
                        background: active ? 'color-mix(in srgb, var(--seed-base) 8%, var(--surface-primary))' : 'transparent',
                        color: active ? 'var(--seed-base)' : 'var(--text-secondary)',
                        border: active ? '1.5px solid color-mix(in srgb, var(--seed-base) 40%, transparent)' : '1px solid var(--border-subtle)',
                      }}
                    >
                      {displayName}
                    </UnstyledButton>
                  );
                })}
              </div>
            </div>
          )}
    </FilterPanelShell>
  );
};
